'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useAnimation } from '@/context/AnimationContext';
import { m, useInView, LazyMotion, domAnimation } from 'framer-motion';
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { BadgeWithIcon } from "@/components/base/badges/badges";
import { Avatar } from "@/components/base/avatar/avatar";
import { api } from '../services/api';
import { analytics } from '../services/analytics';
import SubmissionStatus from './SubmissionStatus';
import { Loader2, Rocket, FileText, MessageSquare, Users } from 'lucide-react';

const ASSET_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '';
const avatar1 = `${ASSET_BASE}/assets/web/banner/avatar_1_small.webp`;
const avatar2 = `${ASSET_BASE}/assets/web/banner/avatar_2_small.webp`;
const avatar3 = `${ASSET_BASE}/assets/web/banner/avatar_3_small.webp`;
const heroImage = `${ASSET_BASE}/assets/web/banner/hero_banner.webp`;

const Hero = ({
    heroImage: customHeroImage,
    avatars: customAvatars
}) => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [message, setMessage] = useState('');

    const resolvedHeroImage = customHeroImage || heroImage;
    const resolvedAvatars = customAvatars || [avatar1, avatar2, avatar3];

    // Access global animation context
    const { isMobile, shouldAnimate: networkIdleAndReady } = useAnimation();

    // Local state for focus/view
    const [isFocused, setIsFocused] = useState(true);
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { amount: 0.1 });

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsFocused(document.visibilityState === 'visible');
        };

        window.addEventListener('visibilitychange', handleVisibilityChange);
        return () => window.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // Animation Logic:
    // Mobile: No complex bg animations (blobs), just static or minimal.
    // Desktop: Run animations if network is idle, in view, and focused.
    const shouldRunAnimations = !isMobile && networkIdleAndReady && isInView && isFocused;

    const handleSubscribe = async () => {
        // Basic validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!email) {
            setStatus('error');
            setMessage('Please enter your email address');
            return;
        }

        if (!emailRegex.test(email)) {
            setStatus('error');
            setMessage('Please enter a valid email address');
            return;
        }

        analytics.trackEvent('Newsletter Subscribe Click', { email });
        setStatus('loading');

        try {
            const response = await api.subscribeNewsletter(email);
            setStatus('success');
            setMessage(response.message);
            analytics.identify(email);
            analytics.trackEvent('Newsletter Subscribe Success', { email });
            setEmail('');
        } catch (error) {
            // Track error internally but show success to user
            analytics.trackEvent('Newsletter Subscribe Error', { email, error: error.message });

            // Optimistic success for user
            setStatus('success');
            setMessage("You've been added to the list!");
            analytics.identify(email);
            setEmail('');
        }
    };

    return (
        <LazyMotion features={domAnimation}>
            <section ref={containerRef} className="relative flex items-center pt-28 pb-16 overflow-hidden" id="home-hero">
                {/* Background: Dot Pattern with Mask */}
                <div
                    className="absolute inset-0 z-0 pointer-events-none text-gray-900/[0.08] dark:text-white/[0.08]"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
                        backgroundSize: '32px 32px',
                        maskImage: 'radial-gradient(circle at 50% 50%, black 20%, transparent 75%)',
                        WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 20%, transparent 75%)',
                    }}
                />

                {/* Animated Gradient Glows */}

                {/* Top Left - Large Drifting Motion */}
                <m.div
                    initial={{ x: -50, y: -50, scale: 1, opacity: 0.2 }}
                    animate={shouldRunAnimations ? {
                        x: [-50, 150, -100, 50, -50],
                        y: [-50, 100, -150, 50, -50],
                        scale: [1, 1.4, 0.8, 1.2, 1],
                        opacity: [0.2, 0.4, 0.2]
                    } : false}
                    transition={{
                        duration: 25,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    className="absolute top-[-20%] left-[-20%] w-[50%] h-[50%] bg-brand-400/30 dark:bg-brand-500/20 blur-[140px] rounded-full z-0 pointer-events-none"
                />

                {/* Center - Breathing & Drifting */}
                <m.div
                    initial={{ x: -30, y: -30, opacity: 0.15, scale: 1 }}
                    animate={shouldRunAnimations ? {
                        x: [-30, 30, -30],
                        y: [-30, 30, -30],
                        opacity: [0.15, 0.35, 0.15],
                        scale: [1, 1.3, 0.9, 1],
                    } : false}
                    transition={{
                        duration: 18,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[60%] h-[60%] bg-secondary-500/30 dark:bg-secondary-600/10 blur-[130px] rounded-full z-0 pointer-events-none"
                />

                {/* Bottom Right - Wide Off-screen Motion */}
                <m.div
                    initial={{ x: 0, y: 0, scale: 1, opacity: 0.2 }}
                    animate={shouldRunAnimations ? {
                        x: [0, -150, 100, -50, 0],
                        y: [0, -100, 150, -50, 0],
                        scale: [1, 1.5, 0.8, 1.2, 1],
                        opacity: [0.2, 0.4, 0.2]
                    } : false}
                    transition={{
                        duration: 30,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                    className="absolute bottom-[-20%] right-[-20%] w-[55%] h-[55%] bg-brand-500/20 dark:bg-brand-600/15 blur-[140px] rounded-full z-0 pointer-events-none"
                />

                <div className="container relative z-10">{/* 960px max-width */}
                    <div className="grid grid-cols-1 lg:grid-cols-[55%_40%] justify-between gap-12 lg:gap-0 items-center">
                        {/* Left Content */}
                        <div className="flex flex-col items-center lg:items-start">
                            {/* Launch Badge */}
                            <BadgeWithIcon
                                type="pill-color"
                                color="gray"
                                size="md"
                                iconLeading={Rocket}
                                className="gap-2 px-3 py-1 rounded-full text-gray-600 ring-gray-100 ring-inset mb-8 dark:text-gray-400"
                            >
                                Launching 2026
                            </BadgeWithIcon>

                            <h1 className="text-4xl md:text-5xl lg:text-[48px] leading-[1.2] tracking-[-0.012em] font-extrabold mb-6 text-center lg:text-left text-gray-900 dark:text-white">
                                Your personalised career growth partner
                            </h1>

                            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-10 leading-relaxed max-w-lg text-center lg:text-left">
                                Build smarter resumes. Ace interviews. Grow faster in your career.
                            </p>

                            {/* Email Form */}
                            <div className="w-full max-w-md mb-10 flex flex-col items-center lg:items-start">
                                {/* Discount Badge */}
                                <BadgeWithIcon
                                    type="pill-color"
                                    color="brand"
                                    size="md"
                                    iconLeading={Rocket}
                                    className="mb-4 gap-2 px-3 py-1"
                                >
                                    Get 50% Off Early Access
                                </BadgeWithIcon>

                                {status === 'success' ? (
                                    <SubmissionStatus
                                        status={status}
                                        successMessage={message}
                                        onReset={() => setStatus('idle')}
                                    />
                                ) : (
                                    <div className="flex flex-col sm:flex-row w-full gap-3">
                                        <div className="flex-1">
                                            <Input
                                                type="email"
                                                placeholder="Enter your email"
                                                size="xl"
                                                value={email}
                                                onChange={setEmail}
                                                isDisabled={status === 'loading'}
                                                className="dark:bg-gray-900/50 dark:border-gray-800"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Button
                                                color="primary"
                                                size="xl"
                                                className="sm:w-auto w-full font-semibold px-8 shadow-lg shadow-brand-600/20"
                                                onClick={handleSubscribe}
                                                isLoading={status === 'loading'}
                                            >
                                                Get Early Access
                                            </Button>

                                            {/* Hand-drawn Arrow next to button - Animated */}
                                            <m.div
                                                initial={isMobile ? { opacity: 1, x: 0, clipPath: "inset(0 0 0 0%)" } : { opacity: 0, x: 20, clipPath: "inset(0 0 0 100%)" }}
                                                animate={isMobile || shouldRunAnimations ? { opacity: 1, x: 0, clipPath: "inset(0 0 0 0%)" } : { opacity: 0, x: 20, clipPath: "inset(0 0 0 100%)" }}
                                                transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
                                                className="hidden lg:block absolute -right-44 -translate-y-1/2 pointer-events-none z-10"
                                            >
                                                <svg width="150" height="150" viewBox="0 0 171 175" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-600 dark:text-brand-400 opacity-80">
                                                    <m.path
                                                        d="M4.60757 64.8552C3.5111 64.9887 2.514 64.2081 2.38048 63.1116C2.24696 62.0152 3.02758 61.0181 4.12405 60.8845L4.36581 62.8699L4.60757 64.8552ZM128.923 72.661L126.98 73.133L126.98 73.1327L128.923 72.661ZM98.8177 98.0212L100.807 97.8114L100.807 97.8116L98.8177 98.0212ZM167.453 110.867C168.509 110.541 169.628 111.133 169.954 112.188C170.28 113.244 169.688 114.363 168.633 114.689L168.043 112.778L167.453 110.867ZM29.9186 81.2621C30.8301 81.886 31.0633 83.1307 30.4395 84.0422C29.8156 84.9537 28.5709 85.1869 27.6594 84.563L28.789 82.9126L29.9186 81.2621ZM3.5503 65.6388L4.67725 63.9865L4.6799 63.9883L3.5503 65.6388ZM3.7896 59.6104L2.79359 57.876L2.79765 57.8737L3.7896 59.6104ZM27.0858 45.0187C28.1527 44.7327 29.2495 45.3658 29.5355 46.4327C29.8215 47.4996 29.1884 48.5963 28.1215 48.8823L27.6037 46.9505L27.0858 45.0187ZM4.36581 62.8699C4.12405 60.8845 4.12471 60.8845 4.12581 60.8843C4.12678 60.8842 4.12834 60.8840 4.13028 60.8838C4.13416 60.8833 4.13983 60.8826 4.14729 60.8817C4.16221 60.8799 4.18427 60.8772 4.21335 60.8738C4.27152 60.8668 4.3578 60.8565 4.4713 60.843C4.6983 60.8161 5.03421 60.7768 5.47196 60.7265C6.34744 60.6261 7.63036 60.4821 9.26417 60.3076C12.5316 59.9586 17.2034 59.4875 22.8272 58.9982C34.0705 58.0199 49.137 56.9673 64.3998 56.6749C79.6385 56.383 95.1752 56.846 107.318 58.9328C113.379 59.9746 118.72 61.441 122.784 63.4952C126.821 65.5358 129.931 68.3334 130.867 72.1893L128.923 72.661L126.98 73.1327C126.444 70.9281 124.559 68.8742 120.98 67.065C117.427 65.2694 112.531 63.8875 106.64 62.875C94.8765 60.8533 79.6417 60.3836 64.4764 60.6742C49.3354 60.9643 34.3657 62.0094 23.1739 62.9831C17.58 63.4698 12.9345 63.9383 9.68896 64.285C8.06629 64.4583 6.79383 64.6011 5.92806 64.7004C5.49518 64.7501 5.16399 64.7889 4.94155 64.8153C4.83033 64.8284 4.7463 64.8385 4.69034 64.8452C4.66236 64.8486 4.6414 64.8511 4.62757 64.8528C4.62065 64.8536 4.61552 64.8542 4.61218 64.8546C4.61051 64.8548 4.60936 64.855 4.60852 64.8551C4.60782 64.8552 4.60782 64.8552 4.36581 62.8699ZM128.923 72.661L130.867 72.1891C131.387 74.3319 131.177 76.3156 130.367 78.1067C129.575 79.8576 128.261 81.3149 126.736 82.5496C123.729 84.9856 119.497 86.8688 115.487 88.5317C111.34 90.2508 107.429 91.7376 104.551 93.4262C101.535 95.1959 100.679 96.597 100.807 97.8114L98.8177 98.0212L96.8287 98.2309C96.4205 94.359 99.4765 91.7661 102.527 89.9763C105.715 88.1052 110.023 86.4667 113.955 84.8367C118.022 83.1503 121.726 81.4606 124.218 79.4415C125.444 78.4488 126.27 77.4581 126.722 76.4582C127.156 75.4986 127.294 74.4288 126.98 73.133L128.923 72.661ZM98.8177 98.0212L100.807 97.8116C101.127 100.852 103.036 103.884 106.591 106.61C110.139 109.332 115.182 111.624 121.361 113.158C133.709 116.224 150.248 116.176 167.453 110.867L168.043 112.778L168.633 114.689C150.782 120.197 133.501 120.294 120.397 117.04C113.85 115.415 108.258 112.93 104.156 109.784C100.06 106.642 97.3012 102.715 96.8287 98.2307L98.8177 98.0212ZM28.789 82.9126L27.6594 84.563L2.4207 67.2892L3.5503 65.6388L4.6799 63.9883L29.9186 81.2621L28.789 82.9126ZM3.5503 65.6388L2.42335 67.291C-0.973525 64.9741 -0.755127 59.914 2.79359 57.876L3.7896 59.6104L4.78561 61.3447C3.78201 61.9211 3.73205 63.3418 4.67725 63.9865L3.5503 65.6388ZM3.7896 59.6104L2.79765 57.8737C6.53734 55.7377 11.8248 52.7468 16.523 50.1962C18.8706 48.9217 21.0846 47.7498 22.8906 46.8474C24.6181 45.9842 26.1711 45.264 27.0858 45.0187L27.6037 46.9505L28.1215 48.8823C27.6747 49.0021 26.5041 49.5134 24.6785 50.4256C22.9316 51.2985 20.763 52.4458 18.4314 53.7116C13.7712 56.2416 8.51311 59.2156 4.78156 61.347L3.7896 59.6104Z"
                                                        initial={{ pathLength: 0 }}
                                                        animate={{ pathLength: 1 }}
                                                        transition={{ duration: 1.5, ease: "easeInOut", delay: 0.8 }}
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </m.div>
                                        </div>

                                    </div>
                                )}
                                {status === 'error' && (
                                    <p className="text-error-600 text-sm mt-2 font-medium">{message}</p>
                                )}
                            </div>

                            {/* Social Proof */}
                            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex -space-x-2">
                                    {resolvedAvatars.map((img, i) => (
                                        <Avatar
                                            key={i}
                                            src={img.src || img}
                                            size="md"
                                            contrastBorder={true}
                                            className="ring-2 ring-white dark:ring-gray-950"
                                        />
                                    ))}
                                </div>
                                <p>Join <span className="font-semibold text-gray-900 dark:text-gray-200">100+ professionals</span> already on board</p>
                            </div>
                        </div>

                        {/* Right Visual */}
                        <div className="relative w-full max-w-[512px] mx-auto lg:mx-0">
                            <div className="relative z-10 w-full aspect-square rounded-tl-[80px] rounded-tr-[16px] rounded-br-[80px] rounded-bl-[16px] overflow-hidden shadow-2xl backdrop-blur-sm">
                                <img
                                    src={resolvedHeroImage.src || resolvedHeroImage}
                                    alt="Professional woman working at a cafe"
                                    fetchPriority="high"
                                    loading="eager"
                                    className="w-full h-auto object-cover aspect-[4/5] lg:aspect-auto"
                                />
                            </div>

                            {/* Floating Badges with Specific Gradient Backgrounds */}
                            <m.div
                                initial={isMobile ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                animate={isMobile || shouldRunAnimations ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                transition={{ delay: 1, duration: 0.6 }}
                                className="absolute top-20 -left-6 lg:-left-20 z-20"
                            >
                                <div className="bg-white/40 dark:bg-gray-900/60 backdrop-blur-xl px-4 py-2.5 rounded-2xl border border-white/20 dark:border-white/10 shadow-md shadow-brand-500/10 flex items-center gap-3">
                                    <FileText className="w-5 h-5 text-brand-600" />
                                    <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">Resume Builder</span>
                                </div>
                            </m.div>

                            {/* <m.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1, duration: 0.6 }}
                            className="absolute top-20 -left-6 lg:-left-25 z-20 p-[1px] rounded-2xl shadow-md shadow-brand-500/10 backdrop-blur-xl"

                        >
                            <div className="rounded-[inherit] p-2 flex items-center gap-3 border border-white/20 ">
                                <FileText className="w-5 h-5 text-brand-600" />
                                <span className="font-semibold text-gray-900 tracking-tight">Resume Builder</span>
                            </div>
                        </m.div> */}

                            <m.div
                                initial={isMobile ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                animate={isMobile || shouldRunAnimations ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                transition={{ delay: 1.2, duration: 0.6 }}
                                className="absolute top-[45%] -left-10 lg:-left-12 z-20"
                            >
                                <div className="bg-white/40 dark:bg-gray-900/60 backdrop-blur-xl px-4 py-2.5 rounded-2xl shadow-md shadow-success-500/10 border border-white/20 dark:border-white/10 flex items-center gap-3">
                                    <MessageSquare className="w-5 h-5 text-success-600" />
                                    <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">Mock Interview</span>
                                </div>
                            </m.div>

                            <m.div
                                initial={isMobile ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                animate={isMobile || shouldRunAnimations ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                transition={{ delay: 1.4, duration: 0.6 }}
                                className="absolute bottom-20 xl:bottom-28 -left-6 lg:-left-20 z-20"
                            >
                                <div className="bg-white/40 dark:bg-gray-900/60 backdrop-blur-xl px-4 py-2.5 rounded-2xl shadow-md shadow-error-500/10 border border-white/20 dark:border-white/10 flex items-center gap-3">
                                    <Users className="w-5 h-5 text-error-600" />
                                    <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">1:1 Mentor Connect</span>
                                </div>
                            </m.div>
                        </div>
                    </div>
                </div >
            </section >
        </LazyMotion>
    );
};

export default Hero;
